import React from 'react';
import commissionPaymentService from '../../../services/ShipmentCommissionPaymentService';
import { X, FileText, Calendar, CreditCard, Hash, MessageSquare, User, Tag } from 'lucide-react';

/**
 * Path: src/components/commission/commission_payment/ViewPaymentModal.js
 *
 * Props:
 *   payment    – payment object (from to_dict)
 *   onClose    – fn
 */
const ViewPaymentModal = ({ payment, onClose }) => {
  if (!payment) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const typeBadgeClass = {
    VEHICLE_OWNER: 'bg-blue-100 text-blue-700 border border-blue-200',
    COMPANY:       'bg-green-100 text-green-700 border border-green-200',
    AGENT:         'bg-purple-100 text-purple-700 border border-purple-200',
  }[payment.recipient_type] || 'bg-gray-100 text-gray-700 border border-gray-200';

  const modeBadgeClass = {
    CASH:          'bg-yellow-100 text-yellow-700',
    BANK_TRANSFER: 'bg-blue-100 text-brand',
    UPI:           'bg-indigo-100 text-indigo-700',
    CHEQUE:        'bg-pink-100 text-pink-700',
    ONLINE:        'bg-cyan-100 text-cyan-700',
  }[payment.payment_mode] || 'bg-gray-100 text-gray-700';

  const InfoRow = ({ icon: Icon, label, value, valueClass = '' }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="w-8 h-8 rounded-sm bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <p className={`text-sm font-semibold text-secondary break-words ${valueClass}`}>{value || 'N/A'}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-secondary">Payment Details</h2>
            <p className="text-xs text-gray-400">{payment.payment_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">

          {/* Amount Hero */}
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-center">
            <p className="text-xs text-blue-600 mb-1">Payment Amount</p>
            <p className="text-3xl font-bold text-blue-600">
              ₹{parseFloat(payment.amount || 0).toLocaleString('en-IN')}
            </p>
          </div>

          {/* Recipient */}
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Recipient</p>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-secondary">{payment.recipient_name || payment.recipient_id}</p>
                  <p className="text-xs text-gray-400">{payment.recipient_id}</p>
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded text-xs font-semibold ${typeBadgeClass}`}>
                {payment.recipient_type?.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Payment Info */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payment Info</p>
            <div className="bg-white border border-gray-200 rounded-sm px-2">
              <InfoRow icon={Calendar} label="Payment Date" value={formatDate(payment.payment_date)} />
              <div className="flex items-start gap-3 py-3 border-b border-gray-100">
                <div className="w-8 h-8 rounded-sm bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CreditCard className="w-4 h-4 text-gray-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-0.5">Payment Mode</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${modeBadgeClass}`}>
                    {payment.payment_mode?.replace('_', ' ') || 'N/A'}
                  </span>
                </div>
              </div>
              <InfoRow icon={Hash}    label="Transaction Reference" value={payment.transaction_ref} />
              <InfoRow icon={Tag}     label="Commission ID"         value={payment.commission_id} />
              <InfoRow icon={MessageSquare} label="Remarks"         value={payment.remarks} />
            </div>
          </div>

          {/* Attachment */}
          {payment.has_attachment && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payment Proof</p>
              <button
                type="button"
                onClick={() => window.open(commissionPaymentService.getFileAttachmentUrl(payment.payment_id), '_blank')}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-sm text-brand hover:bg-brand-light transition-colors text-sm font-medium"
              >
                <FileText className="w-4 h-4" />
                View Attachment — {payment.file_name || 'Open file'}
              </button>
            </div>
          )}

          {/* Audit */}
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Audit</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-400">Created At</p>
                <p className="text-xs font-semibold text-secondary">{formatDateTime(payment.created_at)}</p>
              </div>
              {payment.updated_at && payment.updated_at !== payment.created_at && (
                <div>
                  <p className="text-xs text-gray-400">Last Updated</p>
                  <p className="text-xs font-semibold text-secondary">{formatDateTime(payment.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 font-medium transition-colors text-sm"
          >
            Close
          </button>

        </div>
      </div>
    </div>
  );
};

export default ViewPaymentModal;